import { PageController } from '@client/components/_core/layout/PageController';
import { message } from '@client/components/_ui/feedback/message';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { match } from 'ts-pattern';
import { type MaterialArrangement, MaterialArrangementForm } from './MaterialArrangementForm';

export const Route = createFileRoute('/console/$orgId/mspr/service-providers/details/$id/manage/add')({
    component: AddMaterialArrangement,
    staticData: {
        presentation: 'overlay',
    },
});

function AddMaterialArrangement() {
    const { id: providerId } = Route.useParams();
    const { trpc, trpcUtils } = useTrpc();
    const [open, setOpen] = useState(true);
    const orgId = useOrgId();
    const navigate = useNavigate();
    const [data, setData] = useState<MaterialArrangement>({
        exposureType: '',
        serviceDescription: '',
        contractTitle: '',
        startDate: undefined,
        endDate: undefined,
        criticalOperationId: undefined,
        arrangementCategoryId: undefined,
    });

    const { mutate: createMaterialArrangement, isPending: isCreating } =
        trpc.mspr.materialArrangement.createMaterialArrangement.useMutation();

    const handleSubmit = (values: MaterialArrangement) => {
        setData(values);
        const key = 'create_material_arrangement';
        message.loading({ content: 'Creating…', key });
        createMaterialArrangement(
            {
                exposureType: values.exposureType ?? '',
                serviceDescription: values.serviceDescription ?? '',
                contractTitle: values.contractTitle ?? '',
                startDate: values.startDate ?? new Date(),
                endDate: values.endDate ?? null,
                criticalOperationId: values.criticalOperationId ?? null,
                arrangementCategoryId: values.arrangementCategoryId ?? null,
                providerId: providerId,
            },
            {
                onSuccess: () => {
                    message.success({ content: 'Created', key });
                    trpcUtils.mspr.materialArrangement.listMaterialArrangements.invalidate();
                    setOpen(false);
                },
                onError: (error) => {
                    message.error(error.message || 'Failed to create material arrangement');
                },
            },
        );
    };

    return (
        <PageController route={Route}>
            <MaterialArrangementForm
                open={open}
                mode="add"
                providerId={providerId}
                data={data}
                state={match({ isCreating })
                    .with({ isCreating: true }, () => 'submitting' as const)
                    .otherwise(() => 'ready' as const)}
                onSubmit={handleSubmit}
                onCloseFinished={() => {
                    navigate({
                        to: '/console/$orgId/mspr/service-providers/details/$id/manage',
                        params: { orgId, id: providerId },
                    });
                }}
            />
        </PageController>
    );
}
